import { useState, useEffect, useMemo } from "react";

import { api_without_cancellation } from "../../api/api";
import TreatmentTabs from "../TreatmentPage/TreatmentTabs";
import addIconGold from "../../assets/Add-icon-gold.svg";
import TabsClient from "../new-client-dashboard/Tabs";
import downloadImg from "../../assets/download.svg";
import NewChangesTabs from "../new-client-dashboard/NewChangesTabs";
import CommonTabsAcrossPlatform from "../common/Tabs/CommonTabsAcrossPlatform";

import ReportingDurationModal from "./report-duration-modal";
import ReportValueModal from "./report-value-modal";
import SaveTemplateModal from "./save-template-modal";
import { exportToCSV, exportToPDF, exportToXLSX } from "./utils/export-utils";
import ExportReportModal from "./download-modal";

const ReportingControlsHeader = ({
  currentPage,
  templateActive,
  setTemplateId,
  reportData,
  mainTabs,
}) => {
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [showReportValueModal, setShowReportValueModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);

  // State for saved templates
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [_loading, setLoading] = useState(false);

  // State for current report configuration
  const [currentConfig, setCurrentConfig] = useState({
    duration: {
      date_type: "",
      date_range_start: "",
      date_range_end: "",
      current_day: false,
      current_week: false,
      current_month: false,
      previous_month: false,
      current_quarter: false,
      previous_quarter: false,
      selected_years: [],
    },
    reportValues: {
      client_first_name: false,
      client_last_name: false,
      client_birthday: false,
      client_age: false,
      case_type_category: false,
      case_type: false,
      incident_date: false,
      intake_date: false,
      retained_date: false,
      acceptance_date: false,
      closed_date: false,
      referred_date: false,
      rejected_date: false,
      source_type: false,
      source_name: false,
      secondary_source_type: false,
      secondary_source_name: false,
      third_source_type: false,
      third_source_name: false,
      firm_user_intake: false,
      firm_user_retained: false,
      firm_user_accepted: false,
      firm_user_rejected: false,
      firm_user_referred: false,
    },
  });

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api_without_cancellation.get(
        "/api/firm-reporting/firm-reporting-templates/"
      );
      if (response.status === 200 && response.data?.data) {
        setSavedTemplates(response.data.data?.filter((report) => report?.report_tab === "intake"));
      }
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDurationSave = (durationData) => {
    setCurrentConfig((prev) => ({
      ...prev,
      duration: durationData,
    }));
    setShowDurationModal(false);
  };

  const handleReportValuesSave = (reportValues) => {
    setCurrentConfig((prev) => ({
      ...prev,
      reportValues: reportValues,
    }));
    setShowReportValueModal(false);
  };

  const handleSaveTemplate = async (templateName) => {
    try {
      if (templateActive?.id) {
        const payload = {
          id: templateActive?.id,
          template_name: templateName,
          // Duration
          report_tab: "intake",
          date_type: currentConfig?.duration?.date_type,
          date_range_start: currentConfig.duration.date_range_start || null,
          date_range_end: currentConfig.duration.date_range_end || null,
          current_day: currentConfig.duration.current_day,
          current_week: currentConfig.duration.current_week,
          current_month: currentConfig.duration.current_month,
          previous_month: currentConfig.duration.previous_month,
          current_quarter: currentConfig.duration.current_quarter,
          previous_quarter: currentConfig.duration.previous_quarter,
          selected_years: currentConfig.duration.selected_years,
          // Report value fields
          client_first_name: currentConfig.reportValues.client_first_name,
          client_last_name: currentConfig.reportValues.client_last_name,
          client_birthday: currentConfig.reportValues.client_birthday,
          client_age: currentConfig.reportValues.client_age,
          case_type_category: currentConfig.reportValues.case_type_category,
          case_type: currentConfig.reportValues.case_type,
          incident_date: currentConfig.reportValues.incident_date,
          intake_date: currentConfig.reportValues.intake_date,
          retained_date: currentConfig.reportValues.retained_date,
          acceptance_date: currentConfig.reportValues.acceptance_date,
          closed_date: currentConfig.reportValues.closed_date,
          referred_date: currentConfig.reportValues.referred_date,
          rejected_date: currentConfig.reportValues.rejected_date,
          source_type: currentConfig.reportValues.source_type,
          source_name: currentConfig.reportValues.source_name,
          secondary_source_type: currentConfig.reportValues.secondary_source_type,
          secondary_source_name: currentConfig.reportValues.secondary_source_name,
          third_source_type: currentConfig.reportValues.third_source_type,
          third_source_name: currentConfig.reportValues.third_source_name,
          firm_user_intake: currentConfig.reportValues.firm_user_intake,
          firm_user_retained: currentConfig.reportValues.firm_user_retained,
          firm_user_accepted: currentConfig.reportValues.firm_user_accepted,
          firm_user_rejected: currentConfig.reportValues.firm_user_rejected,
          firm_user_referred: currentConfig.reportValues.firm_user_referred,
        };
        const response = await api_without_cancellation.patch(
          "/api/firm-reporting/firm-reporting-templates/",
          payload
        );

        if (response.status === 201 || response.status === 200) {
          await loadTemplates();
          setShowSaveTemplateModal(false);
        }
      } else {
        const payload = {
          template_name: templateName,
          // Duration fields
          report_tab: "intake",
          date_type: currentConfig?.duration?.date_type,
          date_range_start: currentConfig.duration.date_range_start || null,
          date_range_end: currentConfig.duration.date_range_end || null,
          current_day: currentConfig.duration.current_day,
          current_week: currentConfig.duration.current_week,
          current_month: currentConfig.duration.current_month,
          previous_month: currentConfig.duration.previous_month,
          current_quarter: currentConfig.duration.current_quarter,
          previous_quarter: currentConfig.duration.previous_quarter,
          selected_years: currentConfig.duration.selected_years,
          // Report value fields
          client_first_name: currentConfig.reportValues.client_first_name,
          client_last_name: currentConfig.reportValues.client_last_name,
          client_birthday: currentConfig.reportValues.client_birthday,
          client_age: currentConfig.reportValues.client_age,
          case_type_category: currentConfig.reportValues.case_type_category,
          case_type: currentConfig.reportValues.case_type,
          incident_date: currentConfig.reportValues.incident_date,
          intake_date: currentConfig.reportValues.intake_date,
          retained_date: currentConfig.reportValues.retained_date,
          acceptance_date: currentConfig.reportValues.acceptance_date,
          closed_date: currentConfig.reportValues.closed_date,
          referred_date: currentConfig.reportValues.referred_date,
          rejected_date: currentConfig.reportValues.rejected_date,
          source_type: currentConfig.reportValues.source_type,
          source_name: currentConfig.reportValues.source_name,
          secondary_source_type: currentConfig.reportValues.secondary_source_type,
          secondary_source_name: currentConfig.reportValues.secondary_source_name,
          third_source_type: currentConfig.reportValues.third_source_type,
          third_source_name: currentConfig.reportValues.third_source_name,
          firm_user_intake: currentConfig.reportValues.firm_user_intake,
          firm_user_retained: currentConfig.reportValues.firm_user_retained,
          firm_user_accepted: currentConfig.reportValues.firm_user_accepted,
          firm_user_rejected: currentConfig.reportValues.firm_user_rejected,
          firm_user_referred: currentConfig.reportValues.firm_user_referred,
        };
        const response = await api_without_cancellation.post(
          "/api/firm-reporting/firm-reporting-templates/",
          payload
        );

        if (response.status === 201 || response.status === 200) {
          await loadTemplates();
          setShowSaveTemplateModal(false);
        }
      }
    } catch (error) {
      console.error("Error saving template:", error);
      alert("Failed to save template. Please try again.");
    }
  };

  const handleTemplateClick = (template) => {
    if (templateActive?.id === template?.id) {
      // Reset to default empty state
      setTemplateId(null);
      setCurrentConfig({
        duration: {
          date_type: "",
          date_range_start: "",
          date_range_end: "",
          current_day: false,
          current_week: false,
          current_month: false,
          previous_month: false,
          current_quarter: false,
          previous_quarter: false,
          selected_years: [],
        },
        reportValues: {
          client_first_name: false,
          client_last_name: false,
          client_birthday: false,
          client_age: false,
          case_type_category: false,
          case_type: false,
          incident_date: false,
          intake_date: false,
          retained_date: false,
          acceptance_date: false,
          closed_date: false,
          referred_date: false,
          rejected_date: false,
          source_type: false,
          source_name: false,
          secondary_source_type: false,
          secondary_source_name: false,
          third_source_type: false,
          third_source_name: false,
          firm_user_intake: false,
          firm_user_retained: false,
          firm_user_accepted: false,
          firm_user_rejected: false,
          firm_user_referred: false,
        },
      });
      return;
    } else {
      setTemplateId(template);
      const loadedConfig = {
        duration: {
          date_type: template?.date_type || "",
          date_range_start: template.date_range_start || "",
          date_range_end: template.date_range_end || "",
          current_day: template.current_day || false,
          current_week: template.current_week || false,
          current_month: template.current_month || false,
          previous_month: template.previous_month || false,
          current_quarter: template.current_quarter || false,
          previous_quarter: template.previous_quarter || false,
          selected_years: template.selected_years || [],
        },
        reportValues: {
          client_first_name: template.client_first_name || false,
          client_last_name: template.client_last_name || false,
          client_birthday: template.client_birthday || false,
          client_age: template.client_age || false,
          case_type_category: template.case_type_category || false,
          case_type: template.case_type || false,
          incident_date: template.incident_date || false,
          intake_date: template.intake_date || false,
          retained_date: template.retained_date || false,
          acceptance_date: template.acceptance_date || false,
          closed_date: template.closed_date || false,
          referred_date: template.referred_date || false,
          rejected_date: template.rejected_date || false,
          source_type: template.source_type || false,
          source_name: template.source_name || false,
          secondary_source_type: template.secondary_source_type || false,
          secondary_source_name: template.secondary_source_name || false,
          third_source_type: template.third_source_type || false,
          third_source_name: template.third_source_name || false,
          firm_user_intake: template.firm_user_intake || false,
          firm_user_retained: template.firm_user_retained || false,
          firm_user_accepted: template.firm_user_accepted || false,
          firm_user_rejected: template.firm_user_rejected || false,
          firm_user_referred: template.firm_user_referred || false,
        },
      };

      setCurrentConfig(loadedConfig);
      return;
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      const response = await api_without_cancellation.delete(
        `/api/firm-reporting/firm-reporting-templates/?id=${templateId}`
      );

      if (response.status === 204 || response.status === 200) {
        setSavedTemplates((prev) => prev.filter((t) => t.id !== templateId));
        setCurrentConfig({
          duration: {
            date_type: "",
            date_range_start: "",
            date_range_end: "",
            current_day: false,
            current_week: false,
            current_month: false,
            previous_month: false,
            current_quarter: false,
            previous_quarter: false,
            selected_years: [],
          },
          reportValues: {
            client_first_name: false,
            client_last_name: false,
            client_birthday: false,
            client_age: false,
            case_type_category: false,
            case_type: false,
            incident_date: false,
            intake_date: false,
            retained_date: false,
            acceptance_date: false,
            closed_date: false,
            referred_date: false,
            rejected_date: false,
            source_type: false,
            source_name: false,
            secondary_source_type: false,
            secondary_source_name: false,
            third_source_type: false,
            third_source_name: false,
            firm_user_intake: false,
            firm_user_retained: false,
            firm_user_accepted: false,
            firm_user_rejected: false,
            firm_user_referred: false,
          },
        });
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Failed to delete template. Please try again.");
    }
  };

  const buttonTabs = useMemo(() => {
    const tabs = [];

    // Expert add button with gold icon
    tabs.push({
      id: "duration",
      label: "Duration",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",
      left: "0px",
      onClick: () => setShowDurationModal(true),
      leftHand: true,
      span: (
        <span className="fw-bold p-r-5 ic-15 d-flex align-items-center">
          <img className="d-flex align-items-center justify-content-center" src={addIconGold} />
        </span>
      ),
    });

    tabs.push({
      id: "values",
      label: "Values",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",
      left: "0px",
      onClick: () => setShowReportValueModal(true),
      leftHand: true,
      span: (
        <span className="fw-bold p-r-5 ic-15 d-flex align-items-center">
          <img className="d-flex align-items-center justify-content-center" src={addIconGold} />
        </span>
      ),
    });

    tabs.push({
      id: "save",
      label: "Save",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",
      left: "0px",
      onClick: () => setShowSaveTemplateModal(true),
      leftHand: true,
      span: (
        <span className="fw-bold p-r-5 ic-15 d-flex align-items-center">
          <img className="d-flex align-items-center justify-content-center" src={addIconGold} />
        </span>
      ),
    });

    return tabs;
  }, []);

  const [downloadModal, setDownloadModal] = useState(false);

  const downloadTabs = useMemo(() => {
    const tabs = [];

    // Expert add button with gold icon
    tabs.push({
      id: "download",
      label: "Download",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",
      left: "0px",
      onClick: () => setDownloadModal(true),
      leftHand: true,
      span: (
        <span className="fw-bold p-r-5 ic-15 d-flex align-items-center">
          <img
            className="d-flex align-items-center  justify-content-center"
            style={{
              flexShrink: "0",
            }}
            src={downloadImg}
          />
        </span>
      ),
    });

    return tabs;
  }, []);

  const saveTemplateTabs = useMemo(() => {
    const tabs = [];

    savedTemplates?.forEach((template) => {
      tabs.push({
        id: template?.id,
        label: template?.template_name,
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        left: "0px",
        onClick: () => {
          handleTemplateClick(template);
        },
        rightHand: true,
        span: (
          <span className="fw-bold p-r-5 ic-15 d-flex align-items-center m-l-5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTemplate(template.id);
              }}
              style={{
                height: "15px",
                width: "15px",
                padding: "0",
                border: "none",

                background: "transparent",
                color: "red",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Delete template"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4h6v2" />
              </svg>
            </button>
          </span>
        ),
      });
    });

    return tabs;
  }, [savedTemplates, templateActive, handleTemplateClick, handleDeleteTemplate]);

  const handleExport = async (format, template, data) => {
    switch (format) {
      case "csv":
        exportToCSV(template, data);
        break;
      case "xlsx":
        exportToXLSX(template, data);
        break;
      case "pdf":
        exportToPDF(template, data);
        break;
      default:
        console.error("Unknown export format:", format);
    }
  };

  return (
    <>
      <div
        className="reporting-controls-container d-flex align-items-center justify-content-start"
        style={{ padding: "0px 0px", width: "calc(100% - 100px)" }}
      >
        {/* Control Buttons Row */}
        <div
          className=""
          style={{
            width: "max-content",
          }}
        >
          <NewChangesTabs
            currentTab={currentPage}
            tabsData={mainTabs}
            height={25}
            labelClass=" justify-content-start pe-2"
          />
        </div>
        <div className="add-tabs-section-three-tab">
          <TreatmentTabs
            labelClass="ps-2 pe-5"
            handlePage={currentPage}
            tabsData={buttonTabs}
            height={25}
          />
        </div>

        <CommonTabsAcrossPlatform
          currentTab={templateActive === null ? null : templateActive?.id}
          tabsData={saveTemplateTabs}
          height={25}
          labelClass=" justify-content-center ps-2 pe-2"
        />
        {/* {savedTemplates.length > 0 && (
          <div
            className="template-buttons-row d-flex align-items-center"
            style={{ gap: "5px", flexWrap: "wrap" }}
          >
            {savedTemplates.map((template) => (
              <div
                key={template?.id}
                className="d-flex align-items-center"
                style={{
                  background:
                    templateActive?.id === template?.id ? "var(--primary)" : "var(--primary-10)",
                  border:
                    templateActive?.id === template?.id
                      ? "1px solid var(--primary)"
                      : "1px solid var(--primary-10)",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <button
                  onClick={() => {
                    handleTemplateClick(template);
                  }}
                  style={{
                    height: "25px",
                    padding: "0 12px",
                    border: "none",
                    background: "transparent",
                    color: templateActive?.id === template?.id ? "white" : "var(--primary)",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  className="d-flex align-items-center"
                >
                  {template.template_name}
                </button>
                <button
                  onClick={() => handleDeleteTemplate(template.id)}
                  style={{
                    height: "25px",
                    width: "25px",
                    padding: "0",
                    border: "none",
                    borderLeft:
                      templateActive?.id === template?.id
                        ? "1px solid var(--primary)"
                        : "1px solid var(--primary-10)",
                    background: "transparent",
                    color: "red",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Delete template"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )} */}

        {reportData?.length > 0 && (
          <div style={{ position: "absolute", right: "0px" }}>
            <TabsClient
              tabsData={downloadTabs}
              height={25}
              labelClass="p-l-5 pe-0 justify-content-end me-0"
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <ReportingDurationModal
        show={showDurationModal}
        handleClose={() => setShowDurationModal(false)}
        currentDuration={currentConfig.duration}
        onSave={handleDurationSave}
      />

      <ReportValueModal
        show={showReportValueModal}
        handleClose={() => setShowReportValueModal(false)}
        currentValues={currentConfig.reportValues}
        onSave={handleReportValuesSave}
      />

      <SaveTemplateModal
        templateActive={templateActive}
        show={showSaveTemplateModal}
        handleClose={() => setShowSaveTemplateModal(false)}
        onSave={handleSaveTemplate}
      />

      <ExportReportModal
        show={downloadModal}
        handleClose={() => setDownloadModal(false)}
        template={templateActive}
        reportData={reportData}
        onExport={handleExport}
      />
    </>
  );
};

export default ReportingControlsHeader;
